import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Eye, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Button, Card, Checkbox, EmptyState, Input, Modal, NoticeBox, SegmentedControl, Select, Switch, Tags } from "../../ui";
import { useConfirmedAction } from "../useConfirmedAction";
import { useSubmitGuard } from "../../modals/modalHooks";
import { createAdminFaqAPI, deleteAdminFaqAPI, listAdminFaqsAPI, reorderAdminFaqsAPI, updateAdminFaqAPI } from "../../../utils/api/copilotAdmin";
import { GUIDED_TEXT } from "../../copilot/copilotGuidedText";
import { settingsError } from "./copilotSettingsText";
import "../../copilot/CopilotWidget.css";

const BLANK = { category: "general", visibility: "public", questionAr: "", questionEn: "", answerAr: "", answerEn: "", active: true };
const DEFAULT_FILTERS = { search: "", category: "", visibility: "", status: "", source: "" };
const QUESTION_MAX = 200;
const ANSWER_MAX = 4000;

const topicLabel = (categories, value, lang) => {
  const found = (categories || []).find((c) => c.value === value);
  return found ? (lang === "ar" ? found.labelAr : found.labelEn) : value;
};

// How the FAQ looks in the customer's chat: the same bubbles and buttons as the widget.
function FaqPreview({ t, lang, faq, onClose }) {
  const f = t.faqs;
  const [previewLang, setPreviewLang] = useState(lang);
  const rtl = previewLang === "ar";
  const w = GUIDED_TEXT[previewLang]; // the customer's own wording, in the language being previewed
  return (
    <Modal title={f.previewTitle} onClose={onClose} footer={<Button variant="neutral" appearance="outline" onClick={onClose}>{f.close}</Button>}>
      <div className="cs-lang-bar">
        <span className="cs-muted">{f.previewLang}</span>
        <SegmentedControl size="sm" label={f.previewLang} value={previewLang} onChange={setPreviewLang} options={[{ value: "en", label: t.english }, { value: "ar", label: t.arabic }]} />
      </div>
      <div className="lc-root cs-faq-preview" dir={rtl ? "rtl" : "ltr"} lang={rtl ? "ar" : "en"} data-testid="faq-preview">
        <div className="lc-flow">
          <div className="lc-row user"><div className="lc-flow-bubble me">{faq.question[previewLang] || "…"}</div></div>
          <div className="lc-row assistant"><div className="lc-flow-bubble bot"><p className="lc-answer">{faq.answer[previewLang] || "…"}</p></div></div>
          <div className="lc-row assistant"><div className="lc-flow-bubble bot">{w.didItHelp}</div></div>
          <div className="lc-quick">
            <Button variant="primary" appearance="filled" rounded className="lc-quick-btn" tabIndex={-1} aria-hidden="true">{w.yesHelped}</Button>
            <Button variant="primary" appearance="filled" rounded className="lc-quick-btn" tabIndex={-1} aria-hidden="true">{w.noNeedHelp}</Button>
          </div>
        </div>
      </div>
      <p className="cs-hint">{f.previewVisibility(faq.visibility)}</p>
      {faq.active === false && <NoticeBox variant="warning" size="sm">{f.previewInactive}</NoticeBox>}
    </Modal>
  );
}

const toPreview = (form) => ({ question: { ar: form.questionAr, en: form.questionEn }, answer: { ar: form.answerAr, en: form.answerEn }, visibility: form.visibility, active: form.active });

function FaqEditor({ t, lang, topics, faq, onSaved, onClose }) {
  const confirmation = useConfirmedAction(lang);
  const f = t.faqs;
  const formId = useId();
  const [form, setForm] = useState(
    faq ? { category: faq.category, visibility: faq.visibility, questionAr: faq.question.ar, questionEn: faq.question.en, answerAr: faq.answer.ar, answerEn: faq.answer.en, active: faq.active } : BLANK,
  );
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, run] = useSubmitGuard();

  const topic = topics.find((c) => c.value === form.category);
  const authOnly = topic?.visibility === "authenticated";
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const setCategory = (category) => {
    const next = topics.find((c) => c.value === category);
    if (next?.visibility === "authenticated") set({ category, visibility: "authenticated" });
    else set({ category, ...(authOnly ? { visibility: "public" } : {}) }); // leaving a signed-in only topic: back to public
  };

  const submit = (event) => {
    event.preventDefault();
    const found = {};
    if (!form.questionAr.trim() || !form.questionEn.trim()) found.question = f.questionRequired;
    else if (form.questionAr.length > QUESTION_MAX || form.questionEn.length > QUESTION_MAX) found.question = f.questionLong;
    if (!form.answerAr.trim() || !form.answerEn.trim()) found.answer = f.answerRequired;
    else if (form.answerAr.length > ANSWER_MAX || form.answerEn.length > ANSWER_MAX) found.answer = f.answerLong;
    setErrors(found);
    if (Object.keys(found).length) return;
    confirmation.ask(() => run(async () => {
      try {
        const payload = {
          category: form.category,
          visibility: authOnly ? "authenticated" : form.visibility,
          question: { ar: form.questionAr.trim(), en: form.questionEn.trim() },
          answer: { ar: form.answerAr.trim(), en: form.answerEn.trim() },
          active: form.active,
        };
        if (faq) await updateAdminFaqAPI(faq.id, payload);
        else await createAdminFaqAPI(payload);
        onSaved(faq ? f.updated : f.created);
      } catch (error) {
        setFailure(settingsError(error, t));
        return false;
      }
    }), faq ? "save" : "create", faq ? f.editorTitleEdit : f.editorTitleNew);
  };

  // The message of a group of fields (both languages) is shown once, under the English field.
  const text = (name, label, extra = {}) => (
    <Input label={label} value={form[name]} onChange={(e) => set({ [name]: e.target.value })} error={errors.question ? (name === "questionEn" ? errors.question : true) : false} {...extra} />
  );
  const area = (name, label, extra = {}) => (
    <Input textarea rows={5} label={label} value={form[name]} onChange={(e) => set({ [name]: e.target.value })} error={errors.answer ? (name === "answerEn" ? errors.answer : true) : false} {...extra} />
  );

  return (
    <>
      <Modal
        title={faq ? f.editorTitleEdit : f.editorTitleNew}
        onClose={onClose}
        busy={saving}
        footer={
          <>
            <Button variant="neutral" appearance="outline" icon={<Eye size={14} />} onClick={() => setShowPreview(true)}>{f.preview}</Button>
            <Button variant="neutral" appearance="outline" disabled={saving} onClick={onClose}>{t.cancel}</Button>
            <Button type="submit" form={formId} variant="primary" appearance="filled" loading={saving}>{t.save}</Button>
          </>
        }
      >
        {faq?.source === "system" && !faq.edited && <NoticeBox variant="info" size="sm">{f.seedEdited}</NoticeBox>}
        <form id={formId} className="cs-form" onSubmit={submit} noValidate>
          <div className="cs-field-row">
            <Select label={f.fieldCategory} value={form.category} onChange={(e) => setCategory(e.target.value)} options={topics.map((c) => ({ value: c.value, label: lang === "ar" ? c.labelAr : c.labelEn }))} />
            <Select
              label={f.fieldVisibility}
              value={authOnly ? "authenticated" : form.visibility}
              disabled={authOnly}
              onChange={(e) => set({ visibility: e.target.value })}
              options={[{ value: "public", label: f.publicLabel }, { value: "authenticated", label: f.authenticatedLabel }]}
            />
          </div>
          {authOnly && <p className="cs-hint">{f.authOnlyTopic}</p>}
          {text("questionAr", f.fieldQuestionAr, { dir: "rtl", lang: "ar", maxLength: QUESTION_MAX + 50 })}
          {text("questionEn", f.fieldQuestionEn, { dir: "ltr", lang: "en", maxLength: QUESTION_MAX + 50 })}
          {area("answerAr", f.fieldAnswerAr, { dir: "rtl", lang: "ar" })}
          {area("answerEn", f.fieldAnswerEn, { dir: "ltr", lang: "en" })}
          <p className="cs-hint">{f.noPersonal}</p>
          <Checkbox label={f.fieldActive} checked={form.active} onChange={(e) => set({ active: e.target.checked })} />
          {failure && <EmptyState variant="error" compact>{failure}</EmptyState>}
        </form>
      </Modal>
      {showPreview && <FaqPreview t={t} lang={lang} faq={toPreview(form)} onClose={() => setShowPreview(false)} />}
      {confirmation.dialog}
    </>
  );
}

// FAQs of the chat widget: filter (server side), preview, order, activate and, for managers, create /
// edit / delete. Distinct from the automatic replies and the saved replies, which have their own tabs.
export default function FaqSettings({ t, lang, canManage, onNotice }) {
  const confirmation = useConfirmedAction(lang);
  const f = t.faqs;
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [state, setState] = useState({ status: "loading", items: [], topics: [], error: null });
  const [editor, setEditor] = useState(null); // null | { faq? }
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    const mine = (ticket.current += 1);
    setState((prev) => ({ ...prev, status: prev.items.length ? "ready" : "loading", error: null }));
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const data = await listAdminFaqsAPI(params);
      if (mine === ticket.current) setState({ status: "ready", items: data.faqs, topics: data.categories || [], error: null });
    } catch (error) {
      if (mine === ticket.current) setState((prev) => ({ ...prev, status: prev.items.length ? "ready" : "error", error: settingsError(error, t) }));
    }
  }, [filters, t]);

  useEffect(() => {
    const timer = setTimeout(load, filters.search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, filters.search]);

  const set = (key) => (event) => setFilters((prev) => ({ ...prev, [key]: event.target.value }));
  const filtered = Object.values(filters).some(Boolean);
  // moving is only meaningful when the whole topic is on screen
  const canReorder = canManage && !filters.search && !filters.visibility && !filters.status && !filters.source;

  const toggleActive = (faq) => confirmation.ask(async () => {
    setBusy(faq.id);
    try {
      await updateAdminFaqAPI(faq.id, { active: !faq.active });
      onNotice({ type: "success", text: faq.active ? f.deactivated : f.activated });
      await load();
    } catch (error) {
      onNotice({ type: "error", text: settingsError(error, t) });
      return false;
    } finally {
      setBusy(null);
    }
  }, "toggle", faq.question[lang] || faq.question.en, faq.active, faq.active ? f.deactivated : f.activated);

  const move = (faq, direction) => confirmation.ask(async () => {
    const group = state.items.filter((item) => item.category === faq.category);
    const index = group.findIndex((item) => item.id === faq.id);
    const target = index + direction;
    if (target < 0 || target >= group.length) return;
    const next = [...group];
    [next[index], next[target]] = [next[target], next[index]];
    setBusy(faq.id);
    try {
      await reorderAdminFaqsAPI(next.map((item) => item.id));
      onNotice({ type: "success", text: f.reordered });
      await load();
    } catch (error) {
      onNotice({ type: "error", text: settingsError(error, t) });
      return false;
    } finally {
      setBusy(null);
    }
  }, "reorder", faq.question[lang] || faq.question.en);

  const remove = (faq) => confirmation.ask(async () => {
    try {
      await deleteAdminFaqAPI(faq.id);
      onNotice({ type: "success", text: f.deleted });
      load();
    } catch (error) {
      onNotice({ type: "error", text: settingsError(error, t) });
      return false;
    }
  }, "archive", f.confirmDeleteTitle, true, f.confirmDelete(faq.question[lang] || faq.question.en));

  const sourceTag = (faq) => (faq.source === "system" ? (faq.edited ? f.customized : f.system) : f.custom);
  const questionOf = (faq) => faq.question[lang] || faq.question.en;
  const otherOf = (faq) => faq.question[lang === "ar" ? "en" : "ar"];

  return (
    <div className="cs-stack">
      <Card
        title={f.title}
        subtitle={f.description}
        hoverable={false}
        actions={canManage ? <Button size="sm" variant="primary" appearance="filled" icon={<Plus size={14} />} onClick={() => setEditor({})}>{f.newFaq}</Button> : null}
      >
        {!canManage && <NoticeBox variant="info" size="sm">{t.viewOnly}</NoticeBox>}
        <div className="cs-filters cs-filters-faq" role="search">
          <Input type="search" iconStart={<Search size={16} />} value={filters.search} onChange={set("search")} placeholder={f.search} aria-label={f.search} maxLength={80} />
          <Select
            label={f.category}
            value={filters.category}
            onChange={set("category")}
            options={[{ value: "", label: f.allCategories }, ...state.topics.map((c) => ({ value: c.value, label: lang === "ar" ? c.labelAr : c.labelEn }))]}
          />
          <Select
            label={f.visibility}
            value={filters.visibility}
            onChange={set("visibility")}
            options={[{ value: "", label: f.allVisibility }, { value: "public", label: f.publicLabel }, { value: "authenticated", label: f.authenticatedLabel }]}
          />
          <Select
            label={f.status}
            value={filters.status}
            onChange={set("status")}
            options={[{ value: "", label: f.allStatus }, { value: "active", label: f.active }, { value: "inactive", label: f.inactive }]}
          />
          <Select
            label={f.source}
            value={filters.source}
            onChange={set("source")}
            options={[{ value: "", label: f.allSources }, { value: "system", label: f.system }, { value: "custom", label: f.custom }]}
          />
        </div>
        {canManage && !canReorder && <p className="cs-hint">{f.orderNote}</p>}
      </Card>

      <div aria-busy={state.status === "loading"}>
        {state.status === "loading" && <EmptyState variant="loading" compact>{t.loading}</EmptyState>}
        {state.status === "error" && (
          <EmptyState variant="error" action={<Button size="sm" variant="neutral" appearance="outline" onClick={load}>{t.retry}</Button>}>{state.error}</EmptyState>
        )}
        {state.status === "ready" && state.items.length === 0 && (
          <EmptyState action={filtered ? <Button size="sm" variant="neutral" appearance="outline" onClick={() => setFilters(DEFAULT_FILTERS)}>{f.clearFilters}</Button> : null}>
            {filtered ? f.noMatch : canManage ? f.emptyManager : f.empty}
          </EmptyState>
        )}
        <ul className="cs-reply-grid cs-faq-grid">
          {state.items.map((faq) => {
            const group = state.items.filter((item) => item.category === faq.category);
            const position = group.findIndex((item) => item.id === faq.id);
            const { helpful, notHelpful, escalated } = faq.stats;
            return (
              <li key={faq.id}>
                <Card hoverable={false} className={`cs-reply-card ${faq.active ? "" : "cs-inactive"}`}>
                  <div className="cs-reply-head">
                    <h4>{questionOf(faq)}</h4>
                    <span className="cs-muted" dir={lang === "ar" ? "ltr" : "rtl"}>{otherOf(faq)}</span>
                    <div className="cs-tags">
                      <Tags size="sm" variant="neutral">{topicLabel(state.topics, faq.category, lang)}</Tags>
                      <Tags size="sm" variant={faq.visibility === "authenticated" ? "warning" : "success"}>{faq.visibility === "authenticated" ? f.authenticatedLabel : f.publicLabel}</Tags>
                      <Tags size="sm" variant={faq.source === "system" && !faq.edited ? "info" : "primary"}>{sourceTag(faq)}</Tags>
                      {!faq.active && <Tags size="sm" variant="neutral">{f.inactive}</Tags>}
                    </div>
                  </div>
                  <p className="cs-reply-body" dir={lang === "ar" ? "rtl" : "ltr"}>{faq.answer[lang] || faq.answer.en}</p>
                  <div className="cs-reply-foot">
                    <span className="cs-muted">{helpful + notHelpful + escalated === 0 ? f.statsNone : f.stats(helpful, notHelpful, escalated)}</span>
                  </div>
                  <div className="cs-reply-foot">
                    {canManage ? (
                      <Switch checked={faq.active} disabled={busy === faq.id} onChange={() => toggleActive(faq)} label={faq.active ? f.active : f.inactive} />
                    ) : <span />}
                    <span className="cs-icons">
                      {canReorder && (
                        <>
                          <Button size="sm" variant="neutral" appearance="ghost" aria-label={`${f.moveUp}: ${questionOf(faq)}`} title={f.moveUp} disabled={position === 0 || busy === faq.id} onClick={() => move(faq, -1)} icon={<ArrowUp size={15} />} />
                          <Button size="sm" variant="neutral" appearance="ghost" aria-label={`${f.moveDown}: ${questionOf(faq)}`} title={f.moveDown} disabled={position === group.length - 1 || busy === faq.id} onClick={() => move(faq, 1)} icon={<ArrowDown size={15} />} />
                        </>
                      )}
                      <Button size="sm" variant="neutral" appearance="ghost" aria-label={`${f.preview}: ${questionOf(faq)}`} title={f.preview} onClick={() => setPreview(faq)} icon={<Eye size={15} />} />
                      {canManage && (
                        <>
                          <Button size="sm" variant="neutral" appearance="ghost" aria-label={`${f.edit}: ${questionOf(faq)}`} title={f.edit} onClick={() => setEditor({ faq })} icon={<Pencil size={15} />} />
                          {faq.source === "custom" && (
                            <Button size="sm" variant="danger" appearance="ghost" aria-label={`${f.remove}: ${questionOf(faq)}`} title={f.remove} onClick={() => remove(faq)} icon={<Trash2 size={15} />} />
                          )}
                        </>
                      )}
                    </span>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      </div>

      {editor && (
        <FaqEditor
          t={t}
          lang={lang}
          topics={state.topics}
          faq={editor.faq}
          onClose={() => setEditor(null)}
          onSaved={(text) => { setEditor(null); onNotice({ type: "success", text }); load(); }}
        />
      )}
      {preview && <FaqPreview t={t} lang={lang} faq={preview} onClose={() => setPreview(null)} />}
      {confirmation.dialog}
    </div>
  );
}
